import React, { useState, useRef, useEffect } from 'react';
import { Input, Tooltip, Button, Popover, Typography, Space } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useGrammarChecker } from '../hooks/useGrammarChecker';

const { Text } = Typography;
const { TextArea } = Input;

interface GrammarCheckedTextAreaProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  rows?: number;
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  disabled?: boolean;
  status?: '' | 'error' | 'warning';
  [key: string]: any; // Allow other TextArea props
}

export const GrammarCheckedTextArea: React.FC<GrammarCheckedTextAreaProps> = ({
  value = '',
  onChange,
  onValueChange,
  placeholder,
  rows,
  autoSize,
  disabled,
  status,
  ...restProps
}) => {
  const textAreaRef = useRef<any>(null);
  const { issues, isChecking, isInitialized, applySuggestion } = useGrammarChecker(value, { enabled: !disabled });
  const [showIssues, setShowIssues] = useState(false);
  const [selectedIssueIndex, setSelectedIssueIndex] = useState<number | null>(null);

  // Handle change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e);
    }
    if (onValueChange) {
      onValueChange(e.target.value);
    }
  };

  // Determine status based on grammar issues
  const getStatus = (): '' | 'error' | 'warning' => {
    if (status) return status;
    if (issues.length > 0) return 'warning';
    return '';
  };

  // Get issue at cursor position
  useEffect(() => {
    if (!textAreaRef.current?.resizableTextArea?.textArea) return;
    
    const textArea = textAreaRef.current.resizableTextArea.textArea;
    const handleSelectionChange = () => {
      const cursorPos = textArea.selectionStart;
      const issueAtCursor = issues.findIndex(
        (issue) => cursorPos >= issue.start && cursorPos <= issue.end
      );
      setSelectedIssueIndex(issueAtCursor >= 0 ? issueAtCursor : null);
    };

    textArea.addEventListener('click', handleSelectionChange);
    textArea.addEventListener('keyup', handleSelectionChange);

    return () => {
      textArea.removeEventListener('click', handleSelectionChange);
      textArea.removeEventListener('keyup', handleSelectionChange);
    };
  }, [issues]);

  // Status indicator content
  const statusContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {isChecking ? (
        <Text type="secondary" style={{ fontSize: 12 }}>Checking...</Text>
      ) : issues.length > 0 ? (
        <Tooltip title={`${issues.length} grammar issue${issues.length > 1 ? 's' : ''} found`}>
          <ExclamationCircleOutlined 
            style={{ color: '#faad14', cursor: 'pointer' }}
            onClick={() => setShowIssues(!showIssues)}
          />
        </Tooltip>
      ) : isInitialized && value.trim() ? (
        <Tooltip title="No grammar issues found">
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
        </Tooltip>
      ) : null}
    </div>
  );

  // Issues popover content
  const issuesContent = issues.length > 0 ? (
    <div style={{ maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
      <Typography.Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>
        Grammar Issues ({issues.length})
      </Typography.Title>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {issues.map((issue, index) => (
          <div
            key={index}
            style={{
              padding: 8,
              backgroundColor: selectedIssueIndex === index ? '#e6f7ff' : 'transparent',
              borderRadius: 4,
              border: '1px solid #d9d9d9',
            }}
          >
            <Text strong style={{ fontSize: 12 }}>
              Position {issue.start}-{issue.end}
            </Text>
            <div style={{ marginTop: 4 }}>
              <Text style={{ fontSize: 12 }}>{issue.message}</Text>
            </div>
            {issue.suggestions.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Suggestions:</Text>
                <div style={{ marginTop: 4 }}>
                  {issue.suggestions.slice(0, 3).map((suggestion, sIndex) => (
                    <Button
                      key={sIndex}
                      size="small"
                      type="link"
                      style={{ padding: '2px 4px', fontSize: 11, height: 'auto' }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const newText = await applySuggestion(issue, sIndex);
                          if (newText && newText !== value && onValueChange) {
                            onValueChange(newText);
                            setShowIssues(false);
                          }
                        } catch (error) {
                          console.error('Failed to apply suggestion:', error);
                        }
                      }}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </Space>
    </div>
  ) : null;

  return (
    <div style={{ position: 'relative' }}>
      <TextArea
        {...restProps}
        ref={textAreaRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        autoSize={autoSize}
        disabled={disabled}
        status={getStatus()}
      />
      {isInitialized && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <div style={{ pointerEvents: 'auto' }}>
            {issues.length > 0 ? (
              <Popover
                content={issuesContent}
                title={null}
                trigger="click"
                open={showIssues}
                onOpenChange={setShowIssues}
                placement="topRight"
              >
                {statusContent}
              </Popover>
            ) : (
              statusContent
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GrammarCheckedTextArea;

